<template>
  <div>
    <div class="otb-summary__vehicle-image">
      <viewer-360-image
        v-if="show360Image"
        :show="true"
        :src="vehicleInformation.images"
      />
      <img v-else :src="non360Image" />
    </div>
    <div class="wb-grid-col-mq1-12 wb-grid-col-mq3-10 wb-grid-col-offset-mq3-1">
      <div class="otb-summary__vehicle-status">
        <span class="wb-tag" :class="statusClass">{{ statusLabel }}</span>
        <wb-tooltip-host v-if="showStatusTooltip" :position="tooltipPosition">
          <wb-icon
            class="vehicle-status__icon"
            name="bds/info-circle/24"
          ></wb-icon>
          <wb-tooltip class="wb-type-hint">
            {{ statusHint }}
          </wb-tooltip>
        </wb-tooltip-host>
      </div>
      <p class="otb-summary__vehicle-name wb-type-heading-xs">
        {{ vehicleInformation.modelName }}
      </p>
      <div class="otb-summary__vehicle-setting-list">
        <div
          v-if="motorisationLabel"
          class="otb-summary__vehicle-setting wb-type-copy-tertiary"
        >
          <wb-icon name="bds/engine/24" /> {{ motorisationLabel }}
        </div>
        <div
          v-if="fuelTypeLabel"
          class="otb-summary__vehicle-setting wb-type-copy-tertiary"
        >
          <wb-icon name="bds/fuel/24" /> {{ fuelTypeLabel }}
        </div>
        <div
          v-if="transmissionLabel"
          class="otb-summary__vehicle-setting wb-type-copy-tertiary"
        >
          <wb-icon name="gear" /> {{ transmissionLabel }}
        </div>
      </div>
      <div v-if="showConsumptionLabel" class="otb-summary__vehicle-efficiency">
        <h4 class="otb-summary__vehicle-efficiency-title wb-type-copy-tertiary">
          {{ $t('emicoData.header') }}
        </h4>
      </div>
      <span v-if="showUnspecifiedLabel" class="wb-type-copy-secondary">
        {{ $t('shared.unspecified') }}
      </span>
      <summary-card
        v-if="getVehicleInformation(vehicleInformation).secondary.length"
        :summaryInformation="
          getVehicleInformation(vehicleInformation).secondary
        "
        paddings="none"
        theme="white"
        type="details"
        :path="path"
      />
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent } from 'vue';

import { i18n } from '@dh-io-otb/shared/src/services/translation.service';
import VehicleInformationService from '@dh-io-otb/shared/src/services/components/vehicle-information.service';
import Viewer360Service from '@dh-io-otb/shared/src/services/viewer360.service';
import ImageService from '@dh-io-otb/shared/src/services/image.service';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import { TestDriveVehicle } from '@dh-io-otb/shared/src/graphql/schema';
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import {
  TestDriveMode,
  TestDriveModeSummaryBadgeLabel,
} from '@dh-io-otb/shared/src/models/utilities/testdrive-mode.enum';
import useBooking from '@dh-io-otb/shared/src/composables/booking.composable';

import Viewer360Image from '../../../viewer-360-image/index.vue';
import { SummaryCard } from '../../../';
import { CONTEXT_OPTIONS } from '@dh-io-otb/otbc/src/models/constants';

export default defineComponent({
  name: 'otb-summary-vehicle-info',
  components: {
    SummaryCard,
    Viewer360Image,
  },
  props: {
    status: {
      type: String,
      required: true,
    },
    vehicleInformation: {
      type: Object,
      required: true,
    },
    path: {
      type: String,
    },
    context: {
      type: String,
    },
  },
  setup(props) {
    const { getVehicleInformation } = useBooking();
    const statusClass = computed(() => {
      return {
        'wb-tag--secondary': props.status === TestDriveMode.BOOKING,
        'wb-tag--tertiary': props.status === TestDriveMode.REQUEST,
      };
    });
    const statusLabel = computed(() => {
      return i18n.t(
        `summary.label.${TestDriveModeSummaryBadgeLabel[props.status]}`
      );
    });
    const { isMobile, isSmallScreen } = useMediaQuery();
    const { state } = useStore();
    const showStatusTooltip = computed(() => {
      return state.otbcFlow.testDriveMode === TestDriveMode.REQUEST;
    });
    const tooltipPosition = computed(() => {
      return isSmallScreen.value ? 'bottom' : 'right';
    });
    const statusHint = computed(() => {
      return i18n.t(`summary.hint.underRequest`);
    });
    const fuelTypeLabel = computed(() =>
      props.vehicleInformation.fuelType
        ? i18n.t(
            `shared.fuel.${props.vehicleInformation.fuelType?.toLowerCase()}`
          )
        : ''
    );
    const transmissionLabel = computed(() =>
      props.vehicleInformation.transmission
        ? i18n.t(
            `shared.transmission.${props.vehicleInformation.transmission?.toLowerCase()}`
          )
        : ''
    );
    const motorisationLabel = computed(() =>
      VehicleInformationService.getMotorisationLabel(
        props.vehicleInformation.name,
        props.vehicleInformation.engine
      )
    );
    const consumptionAndCO2Label = computed(() => {
      return VehicleInformationService.getVehicleCO2andConsumption(
        props.vehicleInformation as TestDriveVehicle
      );
    });

    const vansSpecificInformation = computed(() => {
      return VehicleInformationService.getVansSpecificInformation(
        props.vehicleInformation as TestDriveVehicle
      );
    });

    const showConsumptionLabel = computed(
      () =>
        consumptionAndCO2Label.value.length > 0 &&
        props.context !== CONTEXT_OPTIONS.VANS &&
        getVehicleInformation(props.vehicleInformation).secondary.length
    );

    const show360Image = computed(() => {
      return (
        props.vehicleInformation.images &&
        props.vehicleInformation.images.defaultImage &&
        Viewer360Service.is360Image(
          props.vehicleInformation.images.defaultImage
        )
      );
    });

    const non360Image = computed(() => {
      console.log(props.vehicleInformation.images);
      const image =
        props.vehicleInformation.images.defaultImage ||
        props.vehicleInformation.images.fallbackImages.LARGE ||
        props.vehicleInformation.images.fallbackImages.MEDIUM ||
        props.vehicleInformation.images.fallbackImages.SMALL ||
        '';
      return ImageService.clearBBDImage(image);
    });

    const showUnspecifiedLabel = computed(
      () =>
        !fuelTypeLabel.value &&
        !transmissionLabel.value &&
        !motorisationLabel.value &&
        !vansSpecificInformation.value.length &&
        !showConsumptionLabel.value
    );

    return {
      consumptionAndCO2Label,
      vansSpecificInformation,
      fuelTypeLabel,
      non360Image,
      show360Image,
      isMobile,
      showConsumptionLabel,
      showUnspecifiedLabel,
      statusClass,
      statusLabel,
      tooltipPosition,
      statusHint,
      showStatusTooltip,
      transmissionLabel,
      motorisationLabel,
      getVehicleInformation,
    };
  },
});
</script>
<style lang="scss" src="./summary-vehicle-info.scss"></style>
